import { motion } from "framer-motion";
import Tilt from "react-parallax-tilt";
import { Heart, Sparkles, Briefcase, Users, Flame, Cake } from "lucide-react";
import { DividerStar } from "./Filigree";

const services = [
  {
    icon: Heart,
    title: "Wedding Planner",
    desc: "We plan and coordinate beautiful weddings with complete attention to rituals, décor, guest experience and smooth execution. From the first idea to the final moment, we bring your dream wedding to life.",
  },
  {
    icon: Sparkles,
    title: "Reception Décor",
    desc: "Stunning reception setups with elegant stage décor, floral arrangements, lighting, seating design and photo-worthy details that leave a lasting impression on your guests.",
  },
  {
    icon: Briefcase,
    title: "Corporate Events",
    desc: "From business meetings and product launches to company celebrations and formal gatherings, we design professional event experiences that reflect your brand and impress your guests.",
  },
  {
    icon: Users,
    title: "Social Events",
    desc: "Celebrate life's special moments with beautifully planned social events — family functions, engagement parties, anniversaries, get-togethers and private celebrations.",
  },
  {
    icon: Flame,
    title: "Festive Décor",
    desc: "Bring festive occasions to life with vibrant, traditional and elegant décor for Diwali, Navratri, Garba nights, religious functions and cultural celebrations.",
  },
  {
    icon: Cake,
    title: "Birthday Parties",
    desc: "From kids' birthdays to milestone celebrations, we create theme-based party setups with décor, styling and coordination that make every birthday feel extra special.",
  },
];

export default function Services() {
  return (
    <section id="services" className="relative section-pad" data-testid="services-section">
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute top-1/3 left-0 w-[400px] h-[400px] bg-gold/[0.05] blur-[120px] rounded-full" />
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-[#962832]/[0.08] blur-[140px] rounded-full" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-10 bg-gold" />
            <span className="text-xs uppercase tracking-[0.3em] text-gold font-medium">
              Our Expertise
            </span>
            <span className="h-px w-10 bg-gold" />
          </div>
          <h2
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            data-testid="services-heading"
          >
            Elegant event planning for every{" "}
            <span className="italic text-gold-gradient">special</span> occasion.
          </h2>
          <DividerStar className="mt-6" />
          <p className="mt-6 text-ivory/65 text-base md:text-lg leading-relaxed font-light">
            From traditional weddings to modern corporate gatherings, we offer complete
            event planning, décor and coordination services designed to make your
            celebration stress-free, stylish and unforgettable.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: i * 0.06 }}
                data-testid={`service-card-${i}`}
              >
                <Tilt
                  tiltMaxAngleX={8}
                  tiltMaxAngleY={8}
                  glareEnable={true}
                  glareMaxOpacity={0.15}
                  glareColor="#F3E5AB"
                  glarePosition="all"
                  transitionSpeed={1500}
                  className="h-full"
                >
                  <div className="glass-gold rounded-2xl p-7 h-full group hover:bg-gold/[0.06] transition-all duration-500 relative overflow-hidden">
                    <div className="absolute -top-10 -right-10 w-32 h-32 bg-gold/10 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    <div className="relative">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold/30 to-gold/5 border border-gold/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                        <Icon size={22} className="text-gold" strokeWidth={1.5} />
                      </div>
                      <h3 className="font-serif text-2xl text-ivory leading-tight mb-3">
                        {s.title}
                      </h3>
                      <p className="text-ivory/65 text-sm leading-relaxed font-light">
                        {s.desc}
                      </p>
                      <a
                        href="#contact"
                        className="mt-6 inline-flex items-center gap-2 text-gold/90 hover:text-gold text-xs uppercase tracking-[0.2em] transition-all duration-300"
                        data-testid={`service-quote-${i}`}
                      >
                        Get a Quote →
                      </a>
                    </div>
                  </div>
                </Tilt>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
